import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, openCart } from "../../feature/cartSlice";
import { toggleWishlist } from "../../feature/wishlist";
import { Link, useParams } from "react-router-dom";
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaHeart,
  FaLeaf,
  FaSpa,
} from "react-icons/fa";
import {
  FiSearch,
  FiX,
  FiHeart,
  FiShield,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import fallbackImg from "../../assets/Ashwagandha Botanical Supplement Showcase.png";
import { apiFetch, formatDate } from "../../utils/api";

/* ---------------- API ---------------- */
const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

const UPLOAD_PATH = "uploads";

const toImageUrl = (path) => {
  const src = typeof path === "string" ? path : path?.url;
  if (!src) return null;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;

  const clean = String(src).replace(/^\/+/, "");
  // bare filename from the database -> add the uploads folder
  const withFolder = clean.includes("/") ? clean : `${UPLOAD_PATH}/${clean}`;
  return `${BASE_API}/${withFolder}`;
};

/* "a, b, c" or "a\nb" or ["a", {name:"b"}] -> ["a", "b"] */
const toTextList = (v) => {
  if (Array.isArray(v)) {
    return v
      .map((x) =>
        typeof x === "string" ? x : (x?.name ?? x?.label ?? x?.title ?? ""),
      )
      .map((x) => String(x).trim())
      .filter(Boolean);
  }
  if (typeof v === "string") {
    return v
      .split(/\r?\n|,|•/)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
};

/* 1200 -> "1.2k" */
const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(num);
};

/* ---- Demo content (used only when the database fields are empty) ----
   Delete this block and the withDemoContent() call once your real data is filled in. */
const DEMO_ASHWAGANDHA = {
  tagline: "Stress Relief • Energy • Immunity",
  description:
    "Ashwagandha is a powerful adaptogen that helps in reducing stress, improving energy levels and supporting overall well-being. Made with pure, natural ingredients, this supplement is the perfect addition to your daily wellness routine.",
  benefits: [
    "Reduces stress & anxiety",
    "Boosts energy & stamina",
    "Supports immunity",
  ],
  ingredients: [
    "Ashwagandha Root Extract",
    "Black Pepper Extract",
    "Vegetable Cellulose Capsule",
  ],
};

const withDemoContent = (prod) => {
  if (!/ashwagandha/i.test(prod.name)) return prod;
  return {
    ...prod,
    tagline: prod.tagline || DEMO_ASHWAGANDHA.tagline,
    description: prod.description || DEMO_ASHWAGANDHA.description,
    benefits: prod.benefits.length ? prod.benefits : DEMO_ASHWAGANDHA.benefits,
    ingredients: prod.ingredients.length
      ? prod.ingredients
      : DEMO_ASHWAGANDHA.ingredients,
  };
};

/* Converts one API product into the shape this page uses.
   If your API field names are different, change them only here. */
const normalizeProduct = (p) => {
  const price = Number(p.price ?? p.sellingPrice ?? p.salePrice ?? 0);
  // priceMax (database column) is the original price used for the discount
  const mrpRaw =
    p.priceMax ??
    p.pricemax ??
    p.price_max ??
    p.mrp ??
    p.originalPrice ??
    p.oldPrice ??
    null;
  const mrp = mrpRaw && Number(mrpRaw) > price ? Number(mrpRaw) : null;

  const mainImage = toImageUrl(p.image ?? p.img ?? p.thumbnail);
  const gallery = (Array.isArray(p.images) ? p.images : [])
    .map(toImageUrl)
    .filter(Boolean);
  const images = [...new Set([mainImage, ...gallery].filter(Boolean))];
  if (!images.length) images.push(fallbackImg);
  const img = images[0];

  let rawPacks = p.packs ?? p.variants ?? p.sizes;
  // JSON columns can arrive as a string (MariaDB / some MySQL drivers)
  if (typeof rawPacks === "string") {
    try {
      rawPacks = JSON.parse(rawPacks);
    } catch {
      rawPacks = null;
    }
  }
  let packs = [];
  if (Array.isArray(rawPacks) && rawPacks.length) {
    packs = rawPacks.map((x, i) => {
      const packPrice = Number(
        x.price ?? x.sellingPrice ?? x.salePrice ?? price,
      );
      const packMrpRaw = x.priceMax ?? x.mrp ?? x.originalPrice ?? null;
      return {
        label:
          x.label ??
          x.name ??
          x.size ??
          x.title ??
          x.weight ??
          `Option ${i + 1}`,
        price: packPrice,
        mrp:
          packMrpRaw && Number(packMrpRaw) > packPrice
            ? Number(packMrpRaw)
            : null,
      };
    });
  } else {
    packs = [
      {
        label: p.packSize ?? p.size ?? p.weight ?? p.unit ?? "Standard",
        price,
        mrp,
      },
    ];
  }

  const base = {
    id: p.id ?? p._id,
    name: p.name ?? p.title ?? "Untitled product",
    category:
      p.category_name ??
      p.categoryName ??
      p.category?.name ??
      (typeof p.category === "string" ? p.category : ""),
    categoryId: p.category_id ?? p.categoryId ?? p.category?.id ?? null,
    subCategory:
      p.subCategory?.name ??
      p.subCategory ??
      p.subcategory?.name ??
      p.subcategory ??
      "",
    brand: p.brand?.name ?? p.brand ?? "",
    tagline: p.tagline ?? p.subtitle ?? p.shortDescription ?? "",
    rating: Number(p.rating ?? p.averageRating ?? 0),
    reviews: Number(p.reviews ?? p.reviewCount ?? p.numReviews ?? 0),
    img,
    images,
    packs,
    description: p.description ?? p.details ?? "",
    benefits: toTextList(p.Benefits ?? p.benefits),
    ingredients: toTextList(p.Ingredients ?? p.ingredients),
  };

  return withDemoContent(base);
};

/* Accepts [..], { products: [..] }, { data: [..] } or { data: { products: [..] } } */
const extractList = (json) => {
  if (Array.isArray(json)) return json
  if (Array.isArray(json?.products)) return json.products
  if (Array.isArray(json?.categories)) return json.categories   // <-- add this line
  if (Array.isArray(json?.data)) return json.data
  if (Array.isArray(json?.data?.products)) return json.data.products
  if (Array.isArray(json?.data?.categories)) return json.data.categories   // <-- and this one
  if (Array.isArray(json?.items)) return json.items
  return []
}

/* Accepts {..}, { product: {..} } or { data: {..} } */
const extractOne = (json) => {
  if (!json) return null;
  const one = json.product ?? json.data?.product ?? json.data ?? json;
  return one && !Array.isArray(one) && typeof one === "object" ? one : null;
};

/* logged-in user saved by Login / Signup (same key the checkout page uses) */
const getSavedUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

const trustBadges = [
  { Icon: FaLeaf, label: "100% Natural" },
  { Icon: FiShield, label: "No Preservatives" },
  { Icon: FaSpa, label: "Ayurvedic" },
];

function Stars({ value, size = 13 }) {
  return (
    <span className="inline-flex items-center gap-[2px] text-[#f2a916]">
      {[1, 2, 3, 4, 5].map((n) => {
        if (value >= n) return <FaStar key={n} size={size} />;
        if (value >= n - 0.5) return <FaStarHalfAlt key={n} size={size} />;
        return <FaRegStar key={n} size={size} className="text-[#d6c79a]" />;
      })}
    </span>
  );
}

/* Clickable 1-5 star picker used in the review form */
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          onMouseEnter={() => setHover(n)}
          onClick={() => onChange(n)}
          className="p-0.5"
        >
          <FaStar
            size={24}
            className={n <= shown ? "text-[#f2a916]" : "text-[#d6d6d6]"}
          />
        </button>
      ))}
    </div>
  );
}

/* Loading placeholder with the same layout as the real page */
function PageSkeleton() {
  return (
    <div className="w-full bg-[#f7f8f4] pt-[60px] min-h-screen">
      <div className="w-full max-w-[1100px] mx-auto px-4 py-6 sm:py-8 animate-pulse">
        <div className="h-[12px] w-[260px] bg-[#e3e9dd] rounded mb-6" />
        <div className="flex flex-col md:flex-row gap-6 lg:gap-10">
          <div className="w-full md:w-[48%] shrink-0">
            <div className="w-full aspect-square bg-[#eef2e8] rounded-2xl" />
          </div>
          <div className="flex-1 flex flex-col gap-3">
            <div className="h-[30px] w-3/4 bg-[#eef2e8] rounded" />
            <div className="h-[13px] w-1/2 bg-[#eef2e8] rounded" />
            <div className="h-[13px] w-1/3 bg-[#eef2e8] rounded" />
            <div className="h-[28px] w-1/4 bg-[#eef2e8] rounded mt-3" />
            <div className="h-[36px] w-2/3 bg-[#eef2e8] rounded mt-3" />
            <div className="h-[44px] w-full bg-[#eef2e8] rounded-full mt-3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Singleproducts() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const wishItems = useSelector((state) => state.wishlist.items);

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [categories, setCategories] = useState([])

  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("description");
  const [packIdx, setPackIdx] = useState(0);
  const [activeImg, setActiveImg] = useState(0);

  /* Reviews */
  const [reviewData, setReviewData] = useState(null); // { average, count, reviews }
  const [reviewsKey, setReviewsKey] = useState(0);
  const [myRating, setMyRating] = useState(0);
  const [myText, setMyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState(null); // { type: 'success' | 'error', text }
  const isLoggedIn = !!getSavedUser();

  /* Zoom viewer */
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");

  /* "You may also like" carousel */
  const sliderRef = useRef(null);

  /* Fetch this product (/products/:id) and the list (/products) for "You may also like" */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const getJson = async (url) => {
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      return res.json();
    };

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const [singleJson, listJson] = await Promise.all([
          getJson(`${BASE_API}/products/${id}`).catch((e) => {
            if (e.name === "AbortError") throw e;
            return null;
          }),
          getJson(`${BASE_API}/products`).catch((e) => {
            if (e.name === "AbortError") throw e;
            return null;
          }),
        ]);

        const list = listJson
          ? extractList(listJson).map(normalizeProduct)
          : [];
        let current = singleJson ? extractOne(singleJson) : null;
        current = current
          ? normalizeProduct(current)
          : list.find((p) => String(p.id) === String(id));

        if (!current) {
          if (!singleJson && !listJson)
            throw new Error("Could not reach the server.");
          throw new Error("Product not found.");
        }

        setAllProducts(list);
        setProduct(current);
      } catch (err) {
        if (err.name === "AbortError") return;
        setProduct(null);
        setError(
          err.message || "Something went wrong while loading this product.",
        );
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [id, reloadKey]);

  /* Fetch the reviews of this product (public route) */
  useEffect(() => {
    let cancelled = false;
    apiFetch(`/reviews/product/${id}`).then(({ ok, data }) => {
      if (cancelled) return;
      if (ok && data && Array.isArray(data.reviews)) setReviewData(data);
      else setReviewData(null);
    });
    return () => {
      cancelled = true;
    };
  }, [id, reviewsKey]);

  useEffect(() => {
    setQty(1);
    setTab("description");
    setPackIdx(0);
    setActiveImg(0);
    setMyRating(0);
    setMyText("");
    setReviewMsg(null);
    window.scrollTo(0, 0);
  }, [id]);

  const related = useMemo(() => {
  if (!product) return []
  const sameCat = (p) => String(p.categoryId) === String(product.categoryId)
  const same = allProducts.filter((p) => sameCat(p) && p.id !== product.id)
  const others = allProducts.filter((p) => !sameCat(p) && p.id !== product.id)
  return [...same, ...others].slice(0, 8)
}, [product, allProducts])

  const closeZoom = () => {
    setZoomOpen(false);
    setZoomed(false);
    setOrigin("50% 50%");
  };

  const handleMove = (e) => {
    if (!zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    const x = ((point.clientX - rect.left) / rect.width) * 100;
    const y = ((point.clientY - rect.top) / rect.height) * 100;
    setOrigin(
      `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`,
    );
  };

  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [zoomOpen]);

  const slide = (dir) => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  /* Fetch categories so we can show the category name in the breadcrumb */
useEffect(() => {
  const controller = new AbortController()
  fetch(`${BASE_API}/category`, { signal: controller.signal })
    .then((r) => (r.ok ? r.json() : []))
    .then((json) => setCategories(extractList(json)))
    .catch(() => {})
  return () => controller.abort()
}, [])

  /* ---------- loading / error states ---------- */
  if (loading) return <PageSkeleton />;


  if (error || !product) {
    return (
      <div className="w-full bg-[#f7f8f4] pt-[60px] min-h-screen">
        <div className="w-full max-w-[1100px] mx-auto px-4 py-16">
          <div className="bg-white border border-[#e3e9dd] rounded-2xl text-center py-16 px-4">
            <p className="text-[#d9534f] text-[14px] mb-4">
              {error || "Product not found."}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="bg-[#1f6b2e] hover:bg-[#17541f] text-white text-[13px] font-semibold rounded-full px-5 py-2 transition-colors"
              >
                Try again
              </button>
              <Link
                to="/shop"
                className="border border-[#1f6b2e] text-[#1f6b2e] hover:bg-[#1f6b2e] hover:text-white text-[13px] font-semibold rounded-full px-5 py-2 transition-colors"
              >
                Back to Shop
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pack = product.packs[packIdx] || product.packs[0];
  const images =
    product.images && product.images.length ? product.images : [product.img];
  const mainImg = images[activeImg] || images[0];
  const discount = pack.mrp ? Math.round((1 - pack.price / pack.mrp) * 100) : 0;
  const inWishlist = wishItems.some((i) => i.id === product.id);

  // use the real numbers from the reviews table when they are loaded
  const ratingValue = reviewData
    ? Number(reviewData.average) || 0
    : product.rating;
  const reviewCount = reviewData ? reviewData.count : product.reviews;
  const reviewList = reviewData ? reviewData.reviews : [];

  const taglineParts = toTextList(product.tagline.replace(/\s*[•·|]\s*/g, ","));

  const handleAddToCart = () => {
    const showPackLabel = product.packs.length > 1 || pack.label !== "Standard";
    dispatch(
      addToCart({
        id: product.id,
        name: showPackLabel ? `${product.name} (${pack.label})` : product.name,
        price: pack.price,
        image: product.img,
        qty,
      }),
    );
    dispatch(openCart());
    setQty(1);
  };

  const handleWishlist = () =>
    dispatch(
      toggleWishlist({
        id: product.id,
        name: product.name,
        price: pack.price,
        image: product.img,
        category: product.category,
      }),
    );

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewMsg(null);

    if (!myRating) {
      setReviewMsg({ type: "error", text: "Please select a star rating." });
      return;
    }

    try {
      setSubmitting(true);
      const { ok, data } = await apiFetch("/reviews", {
        method: "POST",
        body: {
          product_id: Number(product.id),
          rating: myRating,
          text: myText,
        },
      });

      if (!ok) {
        setReviewMsg({
          type: "error",
          text:
            data?.message ||
            "Could not save your review. Please log in and try again.",
        });
        return;
      }

      setReviewMsg({
        type: "success",
        text: "Thank you! Your review has been saved.",
      });
      setMyRating(0);
      setMyText("");
      setReviewsKey((k) => k + 1); // reload the list and the average
    } catch (err) {
      setReviewMsg({
        type: "error",
        text: "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "description", label: "Description" },
    { key: "benefits", label: "Benefits" },
    { key: "ingredients", label: "Ingredients" },
    { key: "reviews", label: "Reviews" },
  ];

  const onImgError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = fallbackImg;
  };

  const categoryName =
  product.category ||
  categories.find((c) => String(c.id ?? c._id) === String(product.categoryId))
    ?.name ||
  categories.find((c) => String(c.id ?? c._id) === String(product.categoryId))
    ?.title ||
  ''

const crumbs = [
  { label: 'Home', to: '/' },
  ...(categoryName
    ? [
        {
          label: categoryName,
          to: product.categoryId
            ? `/product-category/${product.categoryId}`
            : '/shop',
        },
      ]
    : []),
  ...(product.subCategory ? [{ label: product.subCategory, to: '/shop' }] : []),
]

  return (
    <div className="w-full bg-[#f7f8f4] pt-[60px] min-h-screen">
      <div className="w-full max-w-[1100px] mx-auto px-4 py-6 sm:py-8">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-1.5 text-[12px] text-[#6b7a6d] mb-5">
          {crumbs.map((c, i) => (
            <React.Fragment key={`${c.label}-${i}`}>
              <Link to={c.to} className="hover:text-[#1f6b2e]">
                {c.label}
              </Link>
              <span className="text-[#9aa79c]">&gt;</span>
            </React.Fragment>
          ))}
          <span className="text-[#1f4a2b] font-medium">{product.name}</span>
        </nav>

        {/* Top section: gallery + info */}
        <div className="flex flex-col md:flex-row gap-6 lg:gap-12">
          {/* Gallery */}
          <div className="w-full md:w-[50%] shrink-0 flex flex-col-reverse md:flex-row gap-3">
            {images.length > 1 && (
              <div className="flex md:flex-col gap-2.5 md:w-[56px] shrink-0">
                {images.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImg(i)}
                    aria-label={`Show image ${i + 1}`}
                    className={`w-[52px] h-[52px] md:w-[56px] md:h-[56px] rounded-lg overflow-hidden border bg-white ${
                      i === activeImg
                        ? "border-[#1f6b2e] ring-1 ring-[#1f6b2e]"
                        : "border-[#e3e9dd] hover:border-[#9fbf9a]"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      onError={onImgError}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="relative flex-1 bg-white border border-[#eef1e8] rounded-2xl aspect-square overflow-hidden">
              <img
                src={mainImg}
                alt={product.name}
                onError={onImgError}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                aria-label="Zoom image"
                onClick={() => setZoomOpen(true)}
                className="absolute top-3 right-3 w-[34px] h-[34px] rounded-full bg-white shadow flex items-center justify-center text-[#1f4a2b] hover:bg-[#1f6b2e] hover:text-white transition-colors"
              >
                <FiSearch className="text-[14px]" />
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-[26px] sm:text-[32px] font-bold text-[#1b4d2b] leading-tight">
              {product.name}
            </h1>

            {taglineParts.length > 0 && (
              <p className="text-[13px] font-medium text-[#2f5d3a] mt-2">
                {taglineParts.join(" • ")}
              </p>
            )}

            <div className="flex items-center gap-2 mt-3">
              <Stars value={ratingValue} />
              <button
                type="button"
                onClick={() => setTab("reviews")}
                className="text-[12px] text-[#6b7a6d] hover:text-[#1f6b2e] hover:underline"
              >
                {Number(ratingValue).toFixed(1)} ({formatCount(reviewCount)}{" "}
                reviews)
              </button>
            </div>

            <div className="flex items-baseline flex-wrap gap-3 mt-5">
              <span className="text-[32px] font-bold text-[#1f2d22] leading-none">
                ₹{pack.price}
              </span>
              {pack.mrp && (
                <span className="text-[15px] text-[#9aa79c] line-through">
                  ₹{pack.mrp}
                </span>
              )}
              {discount > 0 && (
                <span className="text-[14px] font-bold text-[#1f6b2e]">
                  {discount}% OFF
                </span>
              )}
            </div>

            {/* Pack size (hidden when the product has only one default option) */}
            {(product.packs.length > 1 || pack.label !== "Standard") && (
              <div className="mt-6">
                <p className="text-[13px] text-[#4b574d] mb-2">Pack Size</p>
                <div className="flex flex-wrap gap-2.5">
                  {product.packs.map((pk, i) => (
                    <button
                      key={`${pk.label}-${i}`}
                      type="button"
                      onClick={() => setPackIdx(i)}
                      className={`px-5 py-2.5 rounded-lg border text-[13px] bg-white transition-colors ${
                        i === packIdx
                          ? "border-[#1f6b2e] text-[#1f4a2b] font-semibold"
                          : "border-[#e3e9dd] text-[#3d4a3f] hover:border-[#1f6b2e]"
                      }`}
                    >
                      {pk.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity + actions */}
            <div className="flex items-center gap-3 mt-6">
              <div className="flex items-center border border-[#e3e9dd] bg-white rounded-full overflow-hidden h-[44px]">
                <button
                  type="button"
                  className="w-[38px] h-full text-[18px] text-[#1f4a2b] hover:bg-[#eef5ea]"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  -
                </button>
                <span className="w-[30px] flex items-center justify-center text-[14px]">
                  {qty}
                </span>
                <button
                  type="button"
                  className="w-[38px] h-full text-[18px] text-[#1f4a2b] hover:bg-[#eef5ea]"
                  onClick={() => setQty((q) => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              <button
                type="button"
                onClick={handleAddToCart}
                className="flex-1 h-[44px] bg-[#1f6b2e] hover:bg-[#17541f] transition-colors text-white text-[14px] font-semibold rounded-full"
              >
                Add to Cart
              </button>

              <button
                type="button"
                onClick={handleWishlist}
                aria-label={
                  inWishlist ? "Remove from wishlist" : "Add to wishlist"
                }
                title={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
                className="w-[44px] h-[44px] shrink-0 rounded-full border border-[#e3e9dd] bg-white flex items-center justify-center text-[#1f4a2b] hover:border-[#d9534f] hover:text-[#d9534f] transition-colors"
              >
                {inWishlist ? (
                  <FaHeart className="text-[16px] text-[#d9534f]" />
                ) : (
                  <FiHeart className="text-[16px]" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mt-8">
          {trustBadges.map(({ Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2.5 h-[40px] px-5 rounded-full bg-white/60 border border-[#eef1e8] shadow-sm"
            >
              <Icon className="text-[15px] text-[#1b6b2e]" />
              <span className="text-[12px] text-[#3d4a3f] whitespace-nowrap">
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-8">
          <div className="flex gap-6 sm:gap-8 border-b border-[#e3e9dd] ">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`pb-3 text-[20px] whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  tab === t.key
                    ? "border-[#1f6b2e] border-b-[4px] text-[#1b4d2b] font-semibold"
                    : "border-transparent text-[#6b7a6d] hover:text-[#1b4d2b]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="pt-5">
            {tab === "description" && (
              <div>
                {product.description ? (
                  <p className="text-[16px] leading-[24px] text-[#4b574d] max-w-[720px]">
                    {product.description}
                  </p>
                ) : (
                  <p className="text-[13px] text-[#6b7a6d]">
                    No description available.
                  </p>
                )}
                {product.benefits.length > 0 && (
                  <ul className="mt-5 flex flex-col gap-3">
                    {product.benefits.slice(0, 3).map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2.5 text-[13px] text-[#3d4a3f]"
                      >
                        <FiCheck className="text-[#1f6b2e] shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === "benefits" &&
              (product.benefits.length > 0 ? (
                <ul className="flex flex-col gap-2.5">
                  {product.benefits.map((b) => (
                    <li
                      key={b}
                      className="flex items-center gap-2.5 text-[13px] text-[#3d4a3f]"
                    >
                      <FiCheck className="text-[#1f6b2e] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-[#6b7a6d]">
                  No benefits listed.
                </p>
              ))}

            {tab === "ingredients" &&
              (product.ingredients.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {product.ingredients.map((ing) => (
                    <span
                      key={ing}
                      className="px-3 py-1.5 rounded-full bg-[#eef5ea] text-[#1f4a2b] text-[12px]"
                    >
                      {ing}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-[#6b7a6d]">
                  No ingredients listed.
                </p>
              ))}

            {tab === "reviews" && (
              <div className="max-w-[720px]">
                {/* Summary */}
                <div className="flex items-center gap-4">
                  <span className="text-[36px] font-bold text-[#1f2d22] leading-none">
                    {Number(ratingValue).toFixed(1)}
                  </span>
                  <div>
                    <Stars value={ratingValue} size={15} />
                    <p className="text-[12px] text-[#6b7a6d] mt-1">
                      Based on {reviewCount} reviews
                    </p>
                  </div>
                </div>

                {/* Write a review */}
                <div className="mt-6 border border-[#e3e9dd] bg-white rounded-xl p-4 sm:p-5">
                  <h3 className="text-[15px] font-semibold text-[#1b4d2b]">
                    Write a review
                  </h3>

                  {isLoggedIn ? (
                    <form
                      onSubmit={submitReview}
                      className="mt-3 flex flex-col gap-3"
                    >
                      <div>
                        <p className="text-[12px] text-[#6b7a6d] mb-1">
                          Your rating
                        </p>
                        <StarPicker value={myRating} onChange={setMyRating} />
                      </div>

                      <textarea
                        rows={4}
                        value={myText}
                        onChange={(e) => setMyText(e.target.value)}
                        maxLength={1000}
                        placeholder="Share what you liked or did not like about this product (optional)"
                        className="w-full border border-[#d6e0d0] rounded-lg px-4 py-3 text-[14px] focus:outline-none focus:border-[#1f6b2e] resize-y"
                      />

                      {reviewMsg && (
                        <p
                          role="alert"
                          className={`text-[13px] ${
                            reviewMsg.type === "success"
                              ? "text-[#1f6b2e]"
                              : "text-red-500"
                          }`}
                        >
                          {reviewMsg.text}
                        </p>
                      )}

                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="h-[42px] px-6 bg-[#1f6b2e] hover:bg-[#17541f] disabled:opacity-60 transition-colors text-white text-[13px] font-semibold rounded-full"
                        >
                          {submitting ? "Saving..." : "Submit review"}
                        </button>
                        <span className="text-[12px] text-[#6b7a6d]">
                          Submitting again updates your earlier review.
                        </span>
                      </div>
                    </form>
                  ) : (
                    <p className="mt-2 text-[13px] text-[#4b574d]">
                      Please{" "}
                      <Link
                        to="/login"
                        className="text-[#1f6b2e] font-semibold hover:underline"
                      >
                        log in
                      </Link>{" "}
                      to write a review.
                    </p>
                  )}
                </div>

                {/* All reviews */}
                <div className="mt-6 flex flex-col gap-4">
                  {reviewList.length === 0 && (
                    <p className="text-[13px] text-[#6b7a6d]">
                      No reviews yet. Be the first to review this product.
                    </p>
                  )}
                  {reviewList.map((r) => (
                    <div
                      key={r.id}
                      className="border-b border-[#e3e9dd] pb-4 last:border-b-0"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[13px] font-semibold text-[#1f2d22]">
                          {r.name}
                        </p>
                        <p className="text-[11px] text-[#9aa79c]">
                          {formatDate(r.createdAt)}
                        </p>
                      </div>
                      <div className="mt-1">
                        <Stars value={r.rating} size={12} />
                      </div>
                      {r.text && (
                        <p className="mt-2 text-[13px] leading-[22px] text-[#4b574d]">
                          {r.text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* You may also like (carousel) */}
        {related.length > 0 && (
          <div className="mt-10 sm:mt-12">
            <h2 className="text-[18px] sm:text-[20px] font-bold text-[#1b4d2b] mb-4">
              You may also like
            </h2>

            <div className="relative">
              <button
                type="button"
                aria-label="Previous products"
                onClick={() => slide(-1)}
                className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-[32px] h-[32px] rounded-full bg-white border border-[#e3e9dd] shadow items-center justify-center text-[#1f4a2b] hover:bg-[#1f6b2e] hover:text-white transition-colors"
              >
                <FiChevronLeft />
              </button>

              <div
                ref={sliderRef}
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="snap-start shrink-0 basis-[46%] sm:basis-[calc((100%-32px)/3)] block bg-white border border-[#e3e9dd] rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="w-full aspect-[4/3] flex items-center justify-center overflow-hidden">
                      <img
                        src={p.img}
                        alt={p.name}
                        onError={onImgError}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <h3 className="text-[13px] font-semibold text-[#1f2d22] mt-3 leading-snug">
                      {p.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Stars value={p.rating} size={10} />
                      <span className="text-[11px] text-[#7a8a7c]">
                        {Number(p.rating).toFixed(1)}
                      </span>
                    </div>
                    <p className="text-[14px] font-bold text-[#1f2d22] mt-1">
                      ₹{p.packs[0].price}
                    </p>
                  </Link>
                ))}
              </div>

              <button
                type="button"
                aria-label="Next products"
                onClick={() => slide(1)}
                className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-[32px] h-[32px] rounded-full bg-white border border-[#e3e9dd] shadow items-center justify-center text-[#1f4a2b] hover:bg-[#1f6b2e] hover:text-white transition-colors"
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Zoom modal */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={closeZoom}
        >
          <button
            type="button"
            aria-label="Close zoom"
            onClick={closeZoom}
            className="absolute top-4 right-4 w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center shadow"
          >
            <FiX className="text-[20px]" />
          </button>

          <div
            className={`relative overflow-hidden ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
            style={{ width: "min(90vw, 85vh)", aspectRatio: "1 / 1" }}
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((z) => !z);
            }}
            onMouseMove={handleMove}
            onTouchMove={handleMove}
          >
            <img
              src={mainImg}
              alt={product.name}
              draggable={false}
              onError={onImgError}
              className="w-full h-full object-contain select-none transition-transform duration-300 ease-out"
              style={{
                transform: zoomed ? "scale(2.5)" : "scale(1)",
                transformOrigin: origin,
              }}
            />
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[12px] px-3 py-1 rounded-full pointer-events-none">
              {zoomed
                ? "Move to look around · Click to zoom out"
                : "Click to zoom in"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default Singleproducts;
